import { useState, type FormEvent } from "react";
import { ArrowUp } from "lucide-react";

interface ComposerProps {
  /** Receives the trimmed text. The field has already emptied itself by the time this runs. */
  onSend: (text: string) => void;
  placeholder: string;
  /** Something is already in flight, so sending again would queue a second question. */
  busy?: boolean;
  autoFocus?: boolean;
  /** Width and placement are the caller's business; the composer does not know where it sits. */
  className?: string;
}

/**
 * Ask GAIA something.
 *
 * One control for both places it appears - the home screen and the conversation itself - which
 * previously carried the same rules in two stylesheets, differing only in a 36px button against a
 * 40px one and in `--color-accent` against `--podpink-500`, which are the same colour. It is 40px
 * here: this app is installed on phones, and the larger target is the one worth keeping.
 *
 * The draft lives here rather than in either screen. Neither had any use for it beyond handing it
 * straight back, and the conversation had to remember to clear it.
 */
export function Composer({
  onSend,
  placeholder,
  busy = false,
  autoFocus = false,
  className = "",
}: ComposerProps) {
  const [value, setValue] = useState("");
  const text = value.trim();
  const canSend = text !== "" && !busy;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSend) return;
    setValue("");
    onSend(text);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`flex w-full items-center gap-3 rounded-full border border-border bg-surface px-4 py-3 shadow-sm focus-within:border-podpink-300 ${className}`}
    >
      <input
        className="min-w-0 flex-1 bg-transparent text-base text-text outline-none placeholder:text-text-muted"
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
      />
      <button
        type="submit"
        disabled={!canSend}
        aria-label="Send"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-45 enabled:hover:opacity-85"
      >
        <ArrowUp className="h-4 w-4" aria-hidden />
      </button>
    </form>
  );
}
